import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Container, Button, Card, CardBody, Row, Col } from 'reactstrap';

import API from 'utils/API';
import Drawer from 'components/Drawer';
import Loader from 'components/Loader';
import ErrorBoundary from 'components/ErrorBoundary';
import PatientDetails from './components/PatientDetails';
import PatientDetailsTab from './components/PatientDetailsTab';
import PatientDetailsTable from './components/PatientDetailsTable';
import ClaimApproveReject from 'components/ClaimApproveReject';

const Patient = () => {
  const { tenantID, patientId } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const [operationType, setOperationType] = useState('');
  const [claimIDs, setClaimIDs] = useState([]);
  const [rxNumbers, setRxNumbers] = useState([]);
  const [patientDetails, setPatientDetails] = useState({});
  const [hasBadRequest, setHasBadRequest] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(true);

  let [claimsIDsObj, setClaimsIDs] = useState({
    closeMatches: [],
    claims: [],
  });

  const fetchPatientDetails = async () => {
    try {
      setLoading(true);
      const { data } = await API.get(
        `/${tenantID}/Patient/${parseInt(patientId)}`
      );
      if (data) {
        setPatientDetails(data);
      }
      setLoading(false);
    } catch ({ response: { status } }) {
      if (status === 400) {
        setHasBadRequest(true);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatientDetails();
  }, []);

  const handleDataLoading = (hasLoading) => {
    setLoading(hasLoading);
  };

  const handleClaimSelect = (ids, type) => {
    if (type) {
      claimsIDsObj.closeMatches = ids;
    } else {
      claimsIDsObj.claims = ids;
      setClaimIDs(ids);
    }

    if (!claimsIDsObj.claims.length) {
      setClaimsIDs({
        closeMatches: [],
        claims: [],
      });
    } else {
      setClaimsIDs(claimsIDsObj);
    }
  };

  const handleRxSelect = (rxNumbers) => {
    setRxNumbers(rxNumbers);
  };

  const toggleDrawer = (type) => {
    setOperationType(type);
    setIsOpen(!isOpen);
  };

  const disabled = loading || hasBadRequest;

  return (
    <>
      <Container fluid className='my-3'>
        <Card className='shadow-top'>
          <CardBody>
            <div className='d-flex align-items-center'>
              <Link
                to='/claims-manager'
                className='text-dark text-decoration-none'
                state={{ reset: true }}
              >
                <FontAwesomeIcon icon={faArrowLeft} className='f-18' />
              </Link>
              <h6 className='f-15 text-primary mb-0 ms-2 d-inline-block'>
                Claims Details
              </h6>
            </div>
            <Row className='align-items-end'>
              <Col>
                <ErrorBoundary>
                  <PatientDetails data={patientDetails} disabled={disabled} />
                </ErrorBoundary>
              </Col>
              <Col xs='auto'>
                <Button
                  color='primary'
                  outline
                  onClick={() => toggleDrawer('reject')}
                  disabled={disabled || !claimIDs.length}
                >
                  Reject
                </Button>
                <Button
                  color='primary'
                  onClick={() => toggleDrawer('approve')}
                  disabled={disabled || !claimIDs.length}
                >
                  Approve
                </Button>
              </Col>
            </Row>
            {loading && <Loader opacity={0.01} color='black' />}
            <ErrorBoundary>
              <PatientDetailsTable
                onDataLoading={handleDataLoading}
                onClaimSelect={handleClaimSelect}
                handleLoading={setLoading}
                onRxSelect={handleRxSelect}
                refresh={refresh}
              />
            </ErrorBoundary>
          </CardBody>
          <CardBody className='px-0'>
            <ErrorBoundary>
              <PatientDetailsTab
                claimIDs={claimIDs}
                claimsIDsObj={claimsIDsObj}
                onDataLoading={handleDataLoading}
                hasInitialLoading={loading}
                onClaimSelect={handleClaimSelect}
                refresh={refresh}
              />
            </ErrorBoundary>
          </CardBody>
        </Card>
      </Container>
      <Drawer
        size='lg'
        direction='end'
        isOpen={isOpen}
        toggle={toggleDrawer}
        title={`Claim - ${
          operationType === 'approve' ? 'Approve' : 'Reject'
        } Selected`}
      >
        <ErrorBoundary>
          <ClaimApproveReject
            ids={claimIDs}
            type={operationType}
            toggle={toggleDrawer}
            rxNumbers={rxNumbers}
            close={() => setIsOpen(false)}
            refetch={() => {
              setClaimIDs({ closeMatches: [], claims: [] });
              setRefresh((pre) => !pre);
              setClaimIDs([]);
            }}
          />
        </ErrorBoundary>
      </Drawer>

      <ToastContainer position='top-right' autoClose={3000} />
    </>
  );
};

export default Patient;
